/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import mapboxgl, { type Map } from 'mapbox-gl';
import { circle } from '@turf/circle';
import { bbox } from '@turf/bbox';

import { defaultPadding, mobilePadding } from 'shared-ui/map';

/** A geographic point in `{ lat, lng }` form. */
export interface FitLocationCenter {
    lat: number;
    lng: number;
}

/** Padding/animation options for {@link fitBoundsToLocation}. */
export interface FitBoundsToLocationOptions {
    /** Use the taller mobile padding (the list drawer overlaps the map bottom). */
    isMobile: boolean;
    /** Mapbox fit speed. Mirrors the shared marker-fit default. */
    speed?: number;
    /** Mapbox fit max duration (ms). Mirrors the shared marker-fit default. */
    maxDuration?: number;
}

/**
 * Frames the map around a single location using a radius (km). Used by the
 * search map when a result set has no markers to fit bounds to (e.g. an empty
 * search or the "searched everywhere, found nothing" US overview).
 *
 * A circle of the given radius is converted to a bounding box so `fitBounds`
 * handles latitude distortion, container size, and padding for us — matching the
 * behavior of the shared marker-bounds fit.
 *
 * @param mapInstance - The Mapbox map to reframe.
 * @param center - The point to center on.
 * @param radiusKm - Radius (km) used to size the framing box.
 * @param options - Padding/animation options.
 */
export function fitBoundsToLocation(
    mapInstance: Map,
    center: FitLocationCenter,
    radiusKm: number,
    { isMobile, speed = 1.25, maxDuration = 4000 }: FitBoundsToLocationOptions
): void {
    try {
        const ring = circle([center.lng, center.lat], radiusKm, {
            units: 'kilometers',
        });
        const [minLng, minLat, maxLng, maxLat] = bbox(ring);
        const bounds = new mapboxgl.LngLatBounds([minLng, minLat], [maxLng, maxLat]);

        mapInstance.fitBounds(bounds, {
            padding: isMobile ? mobilePadding : defaultPadding,
            speed,
            maxDuration,
            offset: [0, 0],
        });
    } catch {
        // THIS TRY-CATCH BLOCK IS IMPORTANT AND SHOULD NOT BE REMOVED!
        // Mapbox can throw 'Invalid LngLat object' during a desktop→mobile
        // resize race between the map's DOM element being removed and the resize
        // listener firing. Mirrors the shared marker-bounds fit's guard.
    }
}
